"use client";

import { Check } from "lucide-react";
import { useState } from "react";
import Link from "next/link";

import { cn } from "@/lib/utils";
import {
  getPlanPricePresentation,
  pricingPlans,
  type BillingInterval,
  type PricingPlan,
} from "@/lib/pricing-data";

import { Button } from "@/components/ui/button";
import PageContent from "@/components/page-content";
import { ContactFormDialog } from "@/components/contact-form-dialog";

const PricingCard = ({
  plan,
  className,
  billingInterval,
  onEnterpriseContact,
}: {
  plan: PricingPlan;
  className?: string;
  billingInterval: BillingInterval;
  onEnterpriseContact?: () => void;
}) => {
  const pricePresentation = getPlanPricePresentation(plan, billingInterval);

  return (
    <div className={cn("flex h-full flex-col", className)}>
      {/* Card top part with fixed height */}
      <div className="h-[360px] flex-none">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="px-8 pb-3 pt-8">
            <h3 className="text-3xl font-semibold">{plan.name}</h3>
          </div>

          {/* Description */}
          <div className="px-8 pb-6">
            <p className="line-clamp-3 text-muted-foreground lg:text-balance">
              {plan.description}
            </p>
          </div>

          {/* Price */}
          <div className="flex grow flex-col justify-start px-8 pb-6">
            <div className="mb-4 flex items-start justify-center">
              <div className="text-center">
                {pricePresentation.isCustom ? (
                  <span className="text-5xl font-semibold">
                    {pricePresentation.price}
                  </span>
                ) : (
                  <div className="flex items-baseline justify-center gap-2">
                    <div className="flex items-start">
                      <span className="mt-2 text-lg font-semibold">$</span>
                      <span className="text-5xl font-semibold">
                        {pricePresentation.price}
                      </span>
                    </div>
                    {"originalPrice" in pricePresentation &&
                      pricePresentation.originalPrice && (
                        <span className="text-lg text-muted-foreground line-through">
                          ${pricePresentation.originalPrice}
                        </span>
                      )}
                  </div>
                )}
                <p className="mt-2 text-sm text-muted-foreground">
                  {pricePresentation.subLabel ?? "\u00A0"}
                </p>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="mt-auto px-8 pb-8">
            {plan.id === "enterprise" && onEnterpriseContact ? (
              <Button
                variant={plan.cta.variant}
                className="w-full cursor-pointer py-6"
                onClick={onEnterpriseContact}
              >
                Contact Us
              </Button>
            ) : (
              <Button variant={plan.cta.variant} className="w-full py-6" asChild>
                <Link href={plan.cta.href}>{plan.cta.text}</Link>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="grow border-t p-8 text-left">
        <ul className="space-y-3">
          {plan.highlights.map((feature, featureIndex) => (
            <li key={featureIndex} className="flex min-h-[40px] items-center gap-3 text-sm">
              <Check className="size-4 shrink-0 text-green-500" />
              <span className="whitespace-pre-line">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

interface PricingOverviewProps {
  className?: string;
  billingInterval: BillingInterval;
  onBillingIntervalChange: (interval: BillingInterval) => void;
}

const PricingOverview = ({
  className,
  billingInterval,
  onBillingIntervalChange,
}: PricingOverviewProps) => {
  const isYearly = billingInterval === "yearly";
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  return (
    <PageContent className={cn("py-12", className)}>
      <div className="mx-auto mb-12 flex items-center justify-center gap-3">
        <div className="inline-flex items-center rounded-full bg-muted p-1 text-sm font-medium">
          <button
            onClick={() => onBillingIntervalChange("monthly")}
            className={cn(
              "rounded-full px-4 py-1.5 transition-colors",
              !isYearly
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Monthly
          </button>
          <button
            onClick={() => onBillingIntervalChange("yearly")}
            className={cn(
              "rounded-full px-4 py-1.5 transition-colors",
              isYearly
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Annual
          </button>
        </div>
      </div>

      {/* Grid layout for pricing tiers */}
      <div className="mx-auto grid max-w-xl rounded-md border md:max-w-none md:grid-cols-4 md:divide-x">
        {pricingPlans.map((plan) => (
          <div
            key={plan.id}
            className={cn(plan.isHighlighted && "bg-muted", "h-full")}
          >
            <PricingCard
              plan={plan}
              billingInterval={billingInterval}
              onEnterpriseContact={() => setContactDialogOpen(true)}
            />
          </div>
        ))}
      </div>

      <p className="mt-6 text-[13px] leading-relaxed text-muted-foreground">
        <Link href="/station" className="underline hover:text-foreground">
          Moondream Station
        </Link>{" "}
        is our free, open-source local inference package with PyTorch and MLX
        support. Moondream Station Pro unlocks access to our high-performance
        inference engine — the same one powering our cloud API — featuring custom
        CUDA kernels, continuous batching, and up to 5x throughput over standard
        PyTorch. Scale plans include one device license, with additional devices
        available at a per-device fee.
      </p>

      <ContactFormDialog
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
      />
    </PageContent>
  );
};

export { PricingOverview };
